import { deriveReelOptionResult, type DerivedSettlementMetadata } from '@s2w/core';
import type { ContestDetail, ContestDetailReelOption, ContestFixture } from '../../../api/contests';

export const contestFixturesById = (
  fixtures: readonly ContestFixture[],
): Map<string, ContestFixture> => new Map(fixtures.map((fixture) => [fixture.id, fixture]));

/** Tab badge + prizes heading: max of configured tiers vs awards (covers partial API payloads). */
export const contestDetailPrizesTabCount = (
  detail: Pick<ContestDetail, 'prizeTiers' | 'prizeAwards'>,
): number => Math.max(detail.prizeTiers?.length ?? 0, detail.prizeAwards?.length ?? 0);

export const settlementLabel = (settlement: {
  status: 'awaiting' | 'settled';
  result?: 'WIN' | 'LOSE' | 'VOID';
}): string => {
  if (settlement.status === 'awaiting') return 'Awaiting settlement';
  if (!settlement.result) return 'Settled';
  return `Settled: ${settlement.result}`;
};

/**
 * The proposition a derived pick was frozen at, as the scoring row shows it:
 * "Over 230", or a spread's side with its signed handicap, "Home -5.5". Without
 * it an Operator scoring a bare "Over" by hand cannot tell which way it went
 * (CAE-1699). Null for a pick settled by an explicit result.
 */
export const reelOptionPropositionLabel = (
  metadata: DerivedSettlementMetadata | null,
): string | null => {
  if (metadata === null) return null;
  if (metadata.settlementType === 'OVER_UNDER') {
    return `${metadata.direction === 'OVER' ? 'Over' : 'Under'} ${metadata.line}`;
  }
  const handicap = metadata.line > 0 ? `+${metadata.line}` : `${metadata.line}`;
  return `${metadata.side === 'HOME' ? 'Home' : 'Away'} ${handicap}`;
};

/**
 * The staged figure of an awaiting option the backend is holding as a push, or
 * null. An over/under whose resulted total lands on its whole-number line is
 * held for an Operator rather than settled (the push rule is still open, Q7),
 * so the row has to say why it is waiting. Read through the same derivation the
 * backend settles with, so the two cannot disagree on what a push is.
 */
export const heldPushFigure = (
  option: Pick<ContestDetailReelOption, 'settlement' | 'settlementMetadata' | 'stagedFigure'>,
): number | null => {
  if (option.settlement.status !== 'awaiting' || option.stagedFigure === null) return null;
  const derivation = deriveReelOptionResult({
    stagedScore: option.stagedFigure,
    settlementMetadata: option.settlementMetadata,
  });
  return derivation.kind === 'push' ? option.stagedFigure : null;
};

export const prizeAwardColor = (
  status: ContestDetail['prizeAwards'][number]['status'],
): 'success' | 'error' | 'info' => {
  if (status === 'completed') return 'success';
  if (status === 'error') return 'error';
  return 'info';
};

export const formatFixtureStatusLabel = (status: ContestFixture['status']) =>
  status.charAt(0).toUpperCase() + status.slice(1);

/*
 * Lives in `contests/` now (CAE-1574): `reel-stats.ts` counts distinct markets
 * by the label the rows actually carry, so the two must read the same function.
 * Re-exported here so the detail tabs keep importing it from where they always
 * have.
 */
export { reelOptionMarketLabel } from '../../../contests/reel-option-market-label';

/*
 * `reelOptionFixtureLabel`, `reelOptionAttachmentChipLabel` and
 * `reelOptionAttachmentAriaLabel` went with CAE-1552.
 *
 * All three existed for `reel-option-attachment-chips.tsx`, the "fixture →
 * market" chip on the old reel panel's rows, and nothing else ever called them.
 * The rebuilt Edit › Reels row says the same thing as two plain spans with a dot
 * between them, and names its fixture through `resolveFixtureLabel` from
 * `@s2w/core` — which the chip's own version predated and quietly disagreed
 * with, since it read `homeTeam vs awayTeam` directly and so ignored both the
 * per-contest display-name override and tournament fixtures with no away team.
 */

export const fixtureStatusChipColor = (
  status: ContestFixture['status'],
): 'warning' | 'error' | 'success' | 'default' => {
  switch (status) {
    case 'scheduled':
      return 'warning';
    case 'live':
      return 'error';
    case 'finished':
      return 'default';
    case 'settled':
      return 'success';
    default:
      return 'default';
  }
};
